/**
 * How it works — the one page that explains the ENTIRE system to a
 * non-technical founder, in plain human English. Founder-commissioned.
 *
 * Sections: the two places (Mac mini ⇄ cloud, with LIVE runner status) ·
 * a day in the life of one task (click-through stepper) · how a landing
 * page gets built (the studio in PuppyPad-Funnels, same stepper) · the team ·
 * where secrets live · safety (the padded room) · what's live right now
 * (real counts pulled from the same endpoints the rest of HQ uses).
 *
 * Kept in sync with the system by rule — the Improver's standing brief
 * (build-spec/IMPROVER-CHARTER.md) checks this page against reality on
 * every run. The footer shows the date of the build it last shipped in.
 */
import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { api } from '../lib/api';
import type { Agent, AgentId, DashboardStats, RunnerStatus, Task } from '@shared/types';

/* ---------- helpers ---------- */
const ago = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} seconds ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

/* ---------- page-scoped styles ----------
   Animations here are decorative; the global reduced-motion rule in theme.css
   (`*{animation:none !important}`) switches them all off. Transitions are
   removed by the local reduced-motion block at the bottom. */
const CSS = `
.hiw{display:grid;gap:40px;max-width:900px}
.hiw section h2{margin-bottom:4px}
.hiw .lede{font-size:14px;color:var(--dim);max-width:640px;margin-bottom:16px}

/* the two places */
.hiw-places{display:grid;grid-template-columns:1fr 120px 1fr;gap:0;align-items:stretch}
.hiw-place{text-align:left;font:inherit;color:inherit;cursor:pointer;background:var(--card-bg);border:1px solid var(--line);border-radius:var(--r);padding:20px;transition:border-color .16s,box-shadow .16s}
.hiw-place:hover{border-color:var(--line-2)}
.hiw-place[aria-expanded="true"]{border-color:rgba(var(--ice-rgb),.5);box-shadow:var(--glow-ice)}
.hiw-place .hint{font-family:var(--mono);font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint);margin-top:12px}
.hiw-place .more{overflow:hidden;max-height:0;opacity:0;transition:max-height .25s ease,opacity .25s ease}
.hiw-place[aria-expanded="true"] .more{max-height:640px;opacity:1}
.hiw-place .more ul{margin:10px 0 0 18px;display:grid;gap:6px;font-size:13px;color:var(--dim)}
.hiw-wire{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:0 6px}
.hiw-wire .line{position:relative;width:100%;height:2px;background:linear-gradient(90deg,rgba(var(--ice-rgb),.1),rgba(var(--ice-rgb),.4),rgba(var(--ice-rgb),.1));border-radius:2px}
.hiw-wire .dot{position:absolute;top:-3px;left:0;width:8px;height:8px;border-radius:50%;background:var(--ice);box-shadow:0 0 10px var(--ice);animation:hiwAsk 8s ease-in-out infinite}
.hiw-wire .say{font-family:var(--mono);font-size:9.5px;letter-spacing:.06em;color:var(--ice);white-space:nowrap;animation:hiwSay 8s ease-in-out infinite}
.hiw-wire .cadence{font-family:var(--mono);font-size:9px;letter-spacing:.08em;color:var(--faint);white-space:nowrap;text-align:center}
@keyframes hiwAsk{0%{left:0;opacity:0}8%{opacity:1}42%{left:calc(100% - 8px)}50%{left:calc(100% - 8px);opacity:1}58%{opacity:1}92%{left:0;opacity:1}100%{left:0;opacity:0}}
@keyframes hiwSay{0%,4%{opacity:0}12%,38%{opacity:1}46%,100%{opacity:0}}
@media (max-width:720px){
  .hiw-places{grid-template-columns:1fr}
  .hiw-wire{padding:14px 0;min-height:74px}
  .hiw-wire .line{width:2px;height:34px;background:linear-gradient(180deg,rgba(var(--ice-rgb),.1),rgba(var(--ice-rgb),.4),rgba(var(--ice-rgb),.1))}
  .hiw-wire .dot{left:-3px;top:0;animation:hiwAskV 8s ease-in-out infinite}
  @keyframes hiwAskV{0%{top:0;opacity:0}8%{opacity:1}42%{top:calc(100% - 8px)}50%{top:calc(100% - 8px);opacity:1}92%{top:0;opacity:1}100%{top:0;opacity:0}}
}

/* stepper */
.hiw-step-dots{display:flex;gap:8px;flex-wrap:wrap;margin:14px 0}
.hiw-step-dots button{width:30px;height:30px;border-radius:50%;border:1px solid var(--line-2);background:transparent;color:var(--dim);font-family:var(--mono);font-size:11px;cursor:pointer;transition:all .16s}
.hiw-step-dots button:hover{border-color:var(--ice);color:var(--ice)}
.hiw-step-dots button[aria-current="step"]{background:var(--ice);border-color:var(--ice);color:var(--btn-primary-text);font-weight:600;box-shadow:var(--glow-ice)}
.hiw-step-dots button.seen{border-color:rgba(var(--ice-rgb),.5);color:var(--ice)}
.hiw-stage{min-height:150px}
.hiw-stage .glyph{font-size:30px;line-height:1;color:var(--ice)}
.hiw-stage h3{margin:10px 0 6px}
.hiw-stage p{font-size:14px;color:var(--dim);max-width:600px}

/* team */
.hiw-team{display:grid;gap:12px;grid-template-columns:repeat(auto-fill,minmax(250px,1fr))}
.hiw-mate{display:flex;gap:14px;align-items:flex-start;padding:16px}
.hiw-mate .face{flex-shrink:0;width:34px;display:flex;justify-content:center;padding-top:2px}
.hiw-mate .glyph{font-size:22px;color:var(--ice);line-height:1.2}
.hiw-mate h3{font-size:14.5px}
.hiw-mate p{font-size:12.5px;color:var(--dim);margin-top:3px}
.hiw-mate .t-label{font-size:9.5px;letter-spacing:.14em;margin-top:2px}

/* secrets */
.hiw-locks{display:grid;gap:12px;grid-template-columns:repeat(auto-fit,minmax(230px,1fr))}
.hiw-lock{padding:18px;text-align:center}
.hiw-lock h3{font-size:14.5px}
.hiw-lock p{font-size:12.5px;color:var(--dim);margin-top:6px}

/* safety */
.hiw-room{border:1px dashed rgba(var(--mint-rgb),.45);border-radius:var(--r-lg);padding:20px;background:rgba(var(--mint-rgb),.04)}
.hiw-room ul{margin:12px 0 0;list-style:none;display:grid;gap:10px}
.hiw-room li{display:flex;gap:10px;align-items:flex-start;font-size:13.5px;color:var(--dim)}
.hiw-room li b{color:var(--text);font-weight:600}
.hiw-room li .tick{color:var(--mint);flex-shrink:0;padding-top:1px}

/* live stats */
.hiw-stats{display:grid;gap:12px;grid-template-columns:repeat(auto-fit,minmax(160px,1fr))}
.hiw-stats .num{font-family:var(--mono);font-size:30px;font-weight:500;margin-top:6px}

.hiw-foot{border-top:1px solid var(--line);padding-top:16px;font-size:12px;color:var(--faint)}
@media (prefers-reduced-motion: reduce){
  .hiw-place .more,.hiw-place,.hiw-step-dots button{transition:none}
}
`;

/* ---------- live runner status (GET /admin/runner, poll 30s) ---------- */
function RunnerPulse() {
  const [runner, setRunner] = useState<RunnerStatus | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get<RunnerStatus>('/admin/runner');
        if (alive) setRunner(r);
      } catch {
        if (alive) setRunner(null);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (runner === undefined) return <p className="small" style={{ marginTop: 14 }}>Checking on the Mac mini…</p>;
  if (runner === null) return <p className="small" style={{ marginTop: 14 }}>Couldn't reach the status check just now — refresh to try again.</p>;

  const color = runner.online ? 'var(--mint)' : runner.last_heartbeat ? 'var(--coral)' : 'var(--faint)';
  const line = runner.online
    ? `The Mac mini is online right now — it last checked in ${runner.last_heartbeat ? ago(runner.last_heartbeat) : 'moments ago'}.`
    : runner.last_heartbeat
      ? `The Mac mini hasn't checked in since ${ago(runner.last_heartbeat)}. The agents pause until it's back — it usually recovers on its own.`
      : `The Mac mini hasn't checked in yet.`;
  return (
    <p className="small" style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 9 }}>
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 9px ${color}`, flexShrink: 0 }} />
      <span>{line} <span className="faint">(live — this line updates every 30 seconds)</span></span>
    </p>
  );
}

/* ---------- section 1: the two places ---------- */
function TwoPlaces() {
  const [open, setOpen] = useState<'mac' | 'cloud' | null>(null);
  const toggle = (k: 'mac' | 'cloud') => setOpen((cur) => (cur === k ? null : k));
  return (
    <section>
      <h2>The two places</h2>
      <p className="lede">
        The whole system lives in exactly two places: a small computer in the office, and a website in the
        cloud. Tap each one to see what's inside.
      </p>
      <div className="hiw-places">
        <button type="button" className="hiw-place" aria-expanded={open === 'mac'} onClick={() => toggle('mac')}>
          <p className="t-label">The Mac mini · the workshop</p>
          <h3 style={{ marginTop: 8 }}>Where the work actually happens</h3>
          <p className="small" style={{ marginTop: 6 }}>
            A real computer sitting in the office. It does the building, the writing, and the clicking.
          </p>
          <div className="more">
            <ul>
              <li><b style={{ color: 'var(--text)' }}>The runner</b> — a little program that keeps asking the cloud "any work for me?" and does whatever it finds.</li>
              <li><b style={{ color: 'var(--text)' }}>The browser hands</b> — a real Chrome browser the agents control, for the tools that don't have a proper back door.</li>
              <li><b style={{ color: 'var(--text)' }}>The working logins and keys</b> — the passwords and API keys the agents work with live in locked files on this machine. The cloud keeps only the few keys its own site needs.</li>
              <li><b style={{ color: 'var(--text)' }}>The landing-page studio</b> — Claude Code, opened in the PuppyPad-Funnels folder. Ask it for a page in plain English and Leo, Iris and Quinn build it right there (see "How a landing page gets built" below).</li>
            </ul>
          </div>
          <p className="hint">{open === 'mac' ? 'tap to close' : 'tap to look inside'}</p>
        </button>

        <div className="hiw-wire" aria-hidden="true">
          <span className="say">"any work for me?"</span>
          <div className="line"><span className="dot" /></div>
          <span className="cadence">asks every 60s</span>
        </div>

        <button type="button" className="hiw-place" aria-expanded={open === 'cloud'} onClick={() => toggle('cloud')}>
          <p className="t-label">The cloud · the noticeboard</p>
          <h3 style={{ marginTop: 8 }}>Where you see and steer it</h3>
          <p className="small" style={{ marginTop: 6 }}>
            This website and its database, hosted on Cloudflare. Open it from your phone, anywhere —
            or tap your browser's <b style={{ color: 'var(--text)' }}>Add to Home Screen</b> to install
            HQ like a real app (full-screen, with its own icon).
          </p>
          <div className="more">
            <ul>
              <li><b style={{ color: 'var(--text)' }}>The HQ site</b> — the pages you're looking at: the feed, the board, approvals, the brains.</li>
              <li><b style={{ color: 'var(--text)' }}>The database</b> — every task, message, card, approval and receipt, kept in one place.</li>
              <li><b style={{ color: 'var(--text)' }}>The to-do list</b> — when you (or the schedule) ask for something, it's pinned here until the Mac mini picks it up.</li>
              <li><b style={{ color: 'var(--text)' }}>The brain chat</b> — the one bit of thinking the cloud does itself: ask a brain a question on the Brains page and the site asks Claude directly, using only that brain's notes. Answers cite their sources, it's capped at 50 questions a day, and every call is logged with its cost.</li>
            </ul>
          </div>
          <p className="hint">{open === 'cloud' ? 'tap to close' : 'tap to look inside'}</p>
        </button>
      </div>
      <p className="small" style={{ marginTop: 12 }}>
        One important detail: the cloud can never reach into the Mac mini. The Mac mini always makes the
        call — it asks the noticeboard for work about once a minute, does the work, and reports back.
      </p>
      <RunnerPulse />
    </section>
  );
}

/* ---------- click-through stepper (sections 2 and 3) ---------- */
type Step = { glyph: string; title: string; body: string };

function Stepper({ steps }: { steps: Step[] }) {
  const [idx, setIdx] = useState(0);
  const step = steps[idx];
  return (
    <div className="card">
      <div className="hiw-step-dots" role="group" aria-label="Steps">
        {steps.map((s, i) => (
          <button
            key={s.title}
            type="button"
            aria-current={i === idx ? 'step' : undefined}
            aria-label={`Step ${i + 1}: ${s.title}`}
            className={i < idx ? 'seen' : ''}
            onClick={() => setIdx(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <div className="hiw-stage">
        <div className="glyph" aria-hidden>{step.glyph}</div>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="button" className="btn btn-line sm" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
          ← Back
        </button>
        {idx < steps.length - 1 ? (
          <button type="button" className="btn btn-primary sm" onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}>
            Next →
          </button>
        ) : (
          <button type="button" className="btn btn-mint sm" onClick={() => setIdx(0)}>
            ↺ Start over
          </button>
        )}
        <span className="small" style={{ alignSelf: 'center', marginLeft: 'auto' }}>
          step {idx + 1} of {steps.length}
        </span>
      </div>
    </div>
  );
}

/* ---------- section 2: a day in the life of one task ---------- */
const STEPS: Step[] = [
  {
    glyph: '◷',
    title: 'A task appears on the noticeboard',
    body: 'It starts in the cloud — either a schedule fires ("it\'s Monday, time to plan this week\'s emails") or a founder asks for something. Either way, a task is pinned to the to-do list in the database, addressed to one agent.',
  },
  {
    glyph: '⇅',
    title: 'The Mac mini picks it up',
    body: 'On its next "any work for me?" check-in — they happen about once a minute — the runner on the Mac mini sees the task and takes it. From here on, everything happens in the workshop.',
  },
  {
    glyph: '◍',
    title: 'The runner becomes the agent',
    body: 'The runner asks Claude, the AI, to be that agent — say, Quinn — and hands over Quinn\'s brain files: her notes, her standards, everything she\'s learned so far. That\'s what makes her act like a colleague with a memory, not a stranger every time.',
  },
  {
    glyph: '⌖',
    title: 'A robot hand on a real browser',
    body: 'If the job needs clicking around a website, the agent uses Playwright — best understood as a robot hand on a real Chrome browser. It opens the page, scrolls, types and clicks exactly like a person would, and sees exactly what a customer would see.',
  },
  {
    glyph: '▤',
    title: 'Receipts get collected',
    body: 'Screenshots, previews and numbers are uploaded to the cloud as the work happens. House rule: a claim without proof doesn\'t count. If an agent says a page is broken, there\'s a picture of it.',
  },
  {
    glyph: '≋',
    title: 'The result lands where you\'ll see it',
    body: 'The agent posts what it did to the Feed and moves its card along the Work Board. Anything that would touch the real world — a real email send, a live page change — stops at Quinn\'s QA check and then waits in Human review for your approve.',
  },
  {
    glyph: '✦',
    title: 'The brain learns',
    body: 'Anything worth remembering — a result, a correction from you, a lesson from a mistake — is written back into the agent\'s brain files. Next task, it starts a little smarter. That\'s the whole loop.',
  },
];

function DayInTheLife() {
  return (
    <section>
      <h2>How an agent works</h2>
      <p className="lede">A day in the life of one task, from the moment it exists to the moment it's done. Click through the steps.</p>
      <Stepper steps={STEPS} />
    </section>
  );
}

/* ---------- section 3: how a landing page gets built (the studio) ---------- */
const STUDIO_STEPS: Step[] = [
  {
    glyph: '✎',
    title: 'You ask in plain English',
    body: 'Open Claude Code in the PuppyPad-Funnels folder on the Mac mini and ask — "build an advertorial for this ad", "make a variation of the listicle", or "go find our winning ads and build from them". You can drop in pages you like as references too. The finished work comes back to whoever asked, in that same chat.',
  },
  {
    glyph: '⌕',
    title: 'Leo does the homework',
    body: 'Leo, the designer and builder, reads the brain first: past pages, test results, the brand, the product. Then he studies the ad itself (so the page carries on exactly what the shopper just saw), our winning ads through StefanBrain and Parker, and fresh examples from design galleries and other brands\' live pages — looked up again on every job, never an old snapshot.',
  },
  {
    glyph: '❝',
    title: 'StefanBrain writes every word',
    body: 'All the copy — every headline, button and FAQ answer — comes from StefanBrain, Stefan Georgi\'s copywriting AI. That\'s a hard rule: Leo designs and builds, but he never writes the words himself.',
  },
  {
    glyph: '◫',
    title: 'Three genuinely different pages',
    body: 'Leo builds three variations, and each one tests a different idea — a different angle, structure and look, not the same page with swapped words. Proven formulas are a toolbox, not a template. Inside each variation, alternative versions of a section can be switched to compare them.',
  },
  {
    glyph: '◐',
    title: 'Iris makes every visual',
    body: 'Photos and graphics come from ChatGPT\'s image maker, video from Google\'s Gemini — both on subscriptions we already pay for, not pay-per-use. Icons and animations are AI-made first, with hand-coding only as the fallback. Each video has a hard ceiling of 200 credits (the aim is 10–20), and the software enforces it.',
  },
  {
    glyph: '◉',
    title: 'Quinn checks everything',
    body: 'Leo and Iris check their own work first. Then Quinn reviews it blind — design, copy, speed, every screen size, every image and video — and sends anything wrong back for them to fix, round after round, until it passes. Bold claims (a vet\'s endorsement, a stock counter) aren\'t blocked: she lists each one under "make it real" — what has to become true before launch.',
  },
  {
    glyph: '▦',
    title: 'You review on a canvas',
    body: 'The three variations land on a Claude canvas — a big zoomable board — at desktop, tablet and phone size, with the "above the fold" line marked and everything clickable. A review note explains the idea being tested, why each section and line is there, and which references inspired it. Approve one, or ask for changes and it goes round again.',
  },
  {
    glyph: '⇪',
    title: 'After you approve: the release',
    body: 'Only then: images and videos are compressed (with no visible quality loss), uploaded to Shopify Files with clean names, and the page goes up on the funnels website with a link for you. You also get a CheckoutChamp paste pack — a zip with the code split into the two boxes CheckoutChamp expects — which a person pastes in.',
  },
  {
    glyph: '✦',
    title: 'The test result teaches the brain',
    body: 'The team runs the A/B test against the current page and sends the numbers back after a week or so. Leo reads them the way a CRO expert would — revenue per visitor, enough sales before calling a winner — and writes the lesson into the brain. When a new lesson clashes with an old one, the newest proven truth wins and the old note is archived.',
  },
];

function PageStudio() {
  return (
    <section>
      <h2>How a landing page gets built</h2>
      <p className="lede">
        Landing pages have their own studio, separate from the task board above: you ask in a chat, and the
        team comes back with three finished pages to compare. The goal is about an hour from ask to canvas.
        Click through the steps.
      </p>
      <Stepper steps={STUDIO_STEPS} />
    </section>
  );
}

/* ---------- section 4: the team ---------- */
const AGENT_BLURBS: Array<{ id: AgentId; role: string; blurb: string }> = [
  { id: 'emma', role: 'Customer support', blurb: 'Answers customers\' emails and messages — our support veteran, joining HQ via a bridge to her existing system.' },
  { id: 'milo', role: 'Email & SMS marketing', blurb: 'Plans, writes and schedules the marketing emails and texts.' },
  { id: 'leo', role: 'Pages & funnels', blurb: 'Designs and builds landing pages — three different variations per request — and tunes checkout funnels so more visitors become buyers.' },
  { id: 'quinn', role: 'Quality — the gate', blurb: 'Checks everyone\'s work like the pickiest customer alive, and can veto anything.' },
  { id: 'iris', role: 'Visuals — makes & reviews', blurb: 'Makes the images, videos, animations and icons for new landing pages, and reviews every image and video against the brief before it goes anywhere near a customer.' },
  { id: 'nora', role: 'Sales calls', blurb: 'Makes sales and "you left something in your cart" phone calls with a natural voice.' },
  { id: 'grace', role: 'Research calls', blurb: 'Calls customers to ask good questions and brings back what they really think.' },
];

function TheTeam() {
  return (
    <section>
      <h2>The team</h2>
      <p className="lede">
        Seven AI colleagues, plus two behind-the-scenes helpers. Each agent keeps its own "brain" — a
        folder of notes it reads before every job and adds to after. The Brains page is that folder made
        browsable: notes sorted onto shelves you can search, edit and reorganise — and you can ask a brain
        questions in plain English. Every answer cites the exact notes it came from, and if the brain
        doesn't know, it says so instead of guessing. (The spinning 3D view is still there, behind the
        "✦ Node view" button.)
      </p>
      <div className="hiw-team">
        {AGENT_BLURBS.map((a) => (
          <div key={a.id} className="card hiw-mate">
            <span className="face"><Orb agent={a.id} size="xs" /></span>
            <div>
              <h3>{a.id.charAt(0).toUpperCase() + a.id.slice(1)}</h3>
              <p className="t-label">{a.role}</p>
              <p>{a.blurb}</p>
            </div>
          </div>
        ))}
        <div className="card hiw-mate">
          <span className="face"><span className="glyph" aria-hidden>⟳</span></span>
          <div>
            <h3>The Improver</h3>
            <p className="t-label">System caretaker</p>
            <p>Visits on a schedule, looks at the whole system with fresh eyes, and fixes or polishes one thing each visit — including keeping this very page honest.</p>
          </div>
        </div>
        <div className="card hiw-mate">
          <span className="face"><span className="glyph" aria-hidden>⇅</span></span>
          <div>
            <h3>The runner</h3>
            <p className="t-label">The stagehand</p>
            <p>Not an agent — the program on the Mac mini that fetches the tasks and gives every agent its hands. No runner, no work.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- section 5: where secrets live ---------- */
/** Little padlock drawing. variant: 'closed' (mint) | 'sealed' (mint, keyhole ring) | 'never' (coral, struck through). */
function LockVis({ variant }: { variant: 'closed' | 'sealed' | 'never' }) {
  const c = variant === 'never' ? 'var(--coral)' : 'var(--mint)';
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden style={{ display: 'block', margin: '0 auto 10px' }}>
      <rect x="10" y="19" width="24" height="17" rx="4" stroke={c} strokeWidth="2" fill={variant === 'never' ? 'none' : 'rgba(var(--mint-rgb),.08)'} />
      <path d="M15 19v-4a7 7 0 0 1 14 0v4" stroke={c} strokeWidth="2" strokeLinecap="round" />
      {variant === 'sealed' ? (
        <>
          <circle cx="22" cy="26.5" r="3.2" stroke={c} strokeWidth="1.8" />
          <path d="M22 29.5v3.5" stroke={c} strokeWidth="1.8" strokeLinecap="round" />
        </>
      ) : (
        <circle cx="22" cy="27.5" r="2.4" fill={c} />
      )}
      {variant === 'never' && <path d="M7 39 37 7" stroke={c} strokeWidth="2.5" strokeLinecap="round" />}
    </svg>
  );
}

function Secrets() {
  return (
    <section>
      <h2>Where the secrets live</h2>
      <p className="lede">
        Passwords and API keys (an API key is a password that programs use) live in two safes on our side —
        and one place they're never allowed near.
      </p>
      <div className="hiw-locks">
        <div className="card hiw-lock">
          <LockVis variant="closed" />
          <h3>On the Mac mini</h3>
          <p>Locked files on the office computer hold the logins the agents work with — the runner's key file, plus the ChatGPT and video logins the landing-page studio uses. They never leave the machine and are never copied anywhere.</p>
        </div>
        <div className="card hiw-lock">
          <LockVis variant="sealed" />
          <h3>Cloudflare's sealed vault</h3>
          <p>The few secrets the cloud side needs are handed to Cloudflare encrypted. Nobody can read them off Cloudflare's dashboard — only the running site can use them, and it shows them to you (and only you) in Settings → Credential Vault.</p>
        </div>
        <div className="card hiw-lock never">
          <LockVis variant="never" />
          <h3>Never in GitHub</h3>
          <p>GitHub is the library where the code is kept. Secrets are never saved there — it's a hard rule the tooling enforces, not a habit we hope to keep.</p>
        </div>
      </div>
      <p className="small" style={{ marginTop: 12 }}>
        One more: the research and copy tools the landing-page studio uses (StefanBrain, Parker, Trendtrack,
        Shopify) are connected through the Claude account's own sign-in, so their keys stay with Claude and
        never touch our files.
      </p>
    </section>
  );
}

/* ---------- section 6: safety ---------- */
function Safety() {
  return (
    <section>
      <h2>Safety — the padded room</h2>
      <p className="lede">
        The agents practise inside walls that make real-world damage impossible, even on their worst day.
        This is what we call safe mode, and it's always on.
      </p>
      <div className="hiw-room">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <p className="t-label" style={{ color: 'var(--mint)' }}>What the walls are made of</p>
          <span className="chip chip-mint"><i />ALWAYS ON</span>
        </div>
        <ul>
          <li><span className="tick" aria-hidden>✓</span><span><b>Emails go only to the seed list</b> — a private test list of our own inboxes. A "send to everyone" simply cannot happen.</span></li>
          <li><span className="tick" aria-hidden>✓</span><span><b>Store changes happen only in the sandbox</b> — a private practice copy of the checkout, paid for with a test card that moves no real money.</span></li>
          <li><span className="tick" aria-hidden>✓</span><span><b>Phone calls go only to the founders' numbers</b> — the voice agents can't dial a customer, full stop.</span></li>
          <li><span className="tick" aria-hidden>✓</span><span><b>Anything real waits for you</b> — it must pass Quinn's QA check, then sit in Human review until a founder taps Approve.</span></li>
          <li><span className="tick" aria-hidden>✓</span><span><b>Landing pages never publish themselves</b> — Shopify uploads and putting a new page live are locked until a founder approves it on the canvas, and pasting into CheckoutChamp is always done by a person.</span></li>
          <li><span className="tick" aria-hidden>✓</span><span><b>Video spend has a hard ceiling</b> — 200 credits per video, checked before every single generation.</span></li>
          <li><span className="tick" aria-hidden>✓</span><span><b>Blocked attempts are stopped loudly</b> — if an agent ever tries to step outside the walls, the attempt is refused and logged where you can see it.</span></li>
        </ul>
      </div>
    </section>
  );
}

/* ---------- section 7: what's live now ---------- */
interface LiveCounts {
  agents: number | null;
  tasksDone: string | null; // '100+' when the list is capped
  brainNotes: number | null;
  runsToday: number | null;
}

function LiveNow() {
  const [counts, setCounts] = useState<LiveCounts | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const [agents, done, brains, stats] = await Promise.allSettled([
        api.get<Agent[]>('/agents'),
        api.get<Task[]>('/tasks?status=done'),
        api.get<Array<{ agent_id: string; count: number }>>('/brains'),
        api.get<DashboardStats>('/dashboard/stats'),
      ]);
      if (!alive) return;
      setCounts({
        agents: agents.status === 'fulfilled' ? agents.value.length : null,
        tasksDone:
          done.status === 'fulfilled'
            ? done.value.length >= 100
              ? '100+'
              : String(done.value.length)
            : null,
        brainNotes: brains.status === 'fulfilled' ? brains.value.reduce((n, b) => n + (b.count || 0), 0) : null,
        runsToday: stats.status === 'fulfilled' ? stats.value.runs_today : null,
      });
    })();
    return () => {
      alive = false;
    };
  }, []);

  const cell = (v: number | string | null) => (v === null || v === undefined ? '—' : String(v));
  return (
    <section>
      <h2>What's live right now</h2>
      <p className="lede">Not a brochure — these numbers come straight from the system the moment you opened this page.</p>
      <div className="hiw-stats">
        <div className="card stat">
          <p className="t-label">Agents on the team</p>
          <div className="num">{counts ? cell(counts.agents) : '…'}</div>
        </div>
        <div className="card stat">
          <p className="t-label">Tasks completed</p>
          <div className="num">{counts ? cell(counts.tasksDone) : '…'}</div>
        </div>
        <div className="card stat">
          <p className="t-label">Notes in agent brains</p>
          <div className="num">{counts ? cell(counts.brainNotes) : '…'}</div>
        </div>
        <div className="card stat">
          <p className="t-label">Runs today</p>
          <div className="num">{counts ? cell(counts.runsToday) : '…'}</div>
        </div>
      </div>
    </section>
  );
}

/* ---------- the page ---------- */
export default function HowItWorks() {
  return (
    <Layout title="How it works">
      <style>{CSS}</style>
      <div className="hiw">
        <p className="lede" style={{ marginBottom: 0, fontSize: 15 }}>
          You have an AI team doing real marketing work. This page explains the whole machine — where it
          lives, how a task gets done, how a landing page gets built, who does what, and why nothing can go
          wrong without you saying yes. Five minutes, no jargon.
        </p>
        <TwoPlaces />
        <DayInTheLife />
        <PageStudio />
        <TheTeam />
        <Secrets />
        <Safety />
        <LiveNow />
        <p className="hiw-foot">
          This page is kept in sync with the system by rule — the Improver checks it against reality on
          every visit. Last verified {__BUILD_DATE__}.
        </p>
      </div>
    </Layout>
  );
}
